import { useContext, useEffect, useState } from "react";
import {
    Box, Card, CircularProgress, Grid, Select, Option, Stack, Typography,
    LinearProgress, Chip, Sheet,
} from "@mui/joy";

import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';

import { usePartner } from "../../PartnerContext";
import { APIGetPartnerStats } from "../../api_partner";


// Бэкенд отдаёт машинные коды — показывать их человеку нельзя
const ITEM_TYPE_LABELS = {
    tariff: 'Подписки (тарифы)',
    likes_pack: 'Пакеты лайков',
    super_likes_pack: 'Пакеты суперлайков',
};
const TARIFF_LABELS = {
    standard: 'Стандарт',
    premium: 'Premium',
    vip: 'VIP',
};
const money = (v) => `${Number(v || 0).toLocaleString('ru-RU')} ₽`;


/**
 * «Сколько заработано с профилей, а сколько с рекламы» — главный вопрос
 * партнёра, и раньше ответа на него не было: выручка с подписок считалась
 * здесь, а доход с рекламы жил на отдельной вкладке. Теперь обе суммы рядом,
 * и берутся они из фактических начислений партнёру (stats.earnings).
 */
function EarningsBlock({ earnings }) {
    if (!earnings) return null;
    const p = earnings.period_totals || {};
    const life = earnings.lifetime || {};

    const rows = [
        { key: 'profiles', label: 'С профилей', hint: 'Подписки, лайки, суперлайки', Icon: PersonRoundedIcon, color: 'primary' },
        { key: 'ads', label: 'С рекламы', hint: 'Реклама на вашей территории', Icon: CampaignRoundedIcon, color: 'warning' },
        { key: 'bonuses', label: 'Бонусы', hint: 'Бонусная программа и ручные начисления', Icon: CardGiftcardRoundedIcon, color: 'success' },
    ];

    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <SavingsRoundedIcon color="success" />
                <Typography level="title-md">Ваш заработок</Typography>
            </Stack>

            <Grid container spacing={1.5}>
                {rows.map(({ key, label, hint, Icon, color }) => (
                    <Grid xs={12} md={4} key={key}>
                        <Card variant="soft" color={color} sx={{ p: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Icon fontSize="small" />
                                <Typography level="body-xs" color="neutral">{label}</Typography>
                            </Stack>
                            <Typography level="h3" sx={{ mt: 0.5 }}>{money(p[key]?.total)}</Typography>
                            <Typography level="body-xs" color="neutral">
                                {hint} • за всё время {money(life[key]?.total)}
                            </Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                sx={{ mt: 1.5 }}
            >
                <Typography level="body-sm">
                    Итого за период: <b>{money(p.total)}</b>
                </Typography>
                <Typography level="body-sm" color="neutral">
                    Доступно к выплате: <b>{money(earnings.balance)}</b> • выплачено {money(earnings.total_paid_out)}
                </Typography>
            </Stack>
        </Card>
    );
}


const PERIODS = [
    { value: 'today', label: 'Сегодня' },
    { value: '7d', label: '7 дней' },
    { value: '30d', label: '30 дней' },
    { value: '90d', label: '90 дней' },
    { value: 'all', label: 'Всё время' },
];


function StatCard({ title, value, hint, color = 'primary' }) {
    return (
        <Card variant="soft" color={color} sx={{ p: 2, minHeight: 90 }}>
            <Typography level="body-xs" color="neutral">{title}</Typography>
            <Typography level="h3" sx={{ mt: 0.5 }}>{value}</Typography>
            {hint && <Typography level="body-xs" color="neutral">{hint}</Typography>}
        </Card>
    );
}


function BreakdownList({ title, items, valueKey = 'count' }) {
    const total = items?.reduce((s, i) => s + (i[valueKey] || 0), 0) || 0;

    return (
        <Card variant="outlined" sx={{ p: 2 }}>
            <Typography level="title-sm" sx={{ mb: 1 }}>{title}</Typography>
            <Stack spacing={1}>
                {items?.map((it, idx) => {
                    const pct = total > 0 ? Math.round((it[valueKey] / total) * 100) : 0;
                    return (
                        <Box key={idx}>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography level="body-sm">{it.label}</Typography>
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                    {it[valueKey]} <Typography level="body-xs" color="neutral">({pct}%)</Typography>
                                </Typography>
                            </Stack>
                            <LinearProgress
                                determinate
                                value={pct}
                                thickness={4}
                                sx={{ mt: 0.3 }}
                            />
                        </Box>
                    );
                })}
                {(!items || !items.length) && (
                    <Typography level="body-xs" color="neutral">Нет данных</Typography>
                )}
            </Stack>
        </Card>
    );
}


export default function PartnerStats() {
    const { partnerToken: token } = usePartner();
    const [period, setPeriod] = useState('30d');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const data = await APIGetPartnerStats(token, period);
                setStats(data?.stats || null);
            } catch (e) {
                setStats(null);
            } finally {
                setLoading(false);
            }
        };
        if (token) load();
    }, [token, period]);

    if (loading) {
        return (
            <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress />
            </Stack>
        );
    }

    if (!stats) {
        return <Typography color="danger">Не удалось загрузить статистику</Typography>;
    }

    return (
        <Stack spacing={2}>
            {/* Период */}
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Chip color="primary" variant="soft">
                    {stats.scope?.city || stats.scope?.region || 'Ваш регион'}
                </Chip>
                <Select
                    size="sm"
                    value={period}
                    onChange={(_, v) => setPeriod(v)}
                    sx={{ minWidth: 140 }}
                >
                    {PERIODS.map(p => <Option key={p.value} value={p.value}>{p.label}</Option>)}
                </Select>
            </Stack>

            {/* Заработок партнёра: с профилей / с рекламы / бонусы */}
            <EarningsBlock earnings={stats.earnings} />

            {/* Карточки */}
            <Grid container spacing={1.5}>
                <Grid xs={6} md={3}>
                    <StatCard
                        title="Всего профилей"
                        value={stats.users?.total ?? 0}
                        hint={`+${stats.users?.new_in_period ?? 0} за период`}
                    />
                </Grid>
                <Grid xs={6} md={3}>
                    <StatCard
                        title="Оборот на территории"
                        value={money(stats.revenue?.total)}
                        hint={`${stats.revenue?.count ?? 0} платежей • ваш заработок ниже`}
                        color="success"
                    />
                </Grid>
                <Grid xs={6} md={3}>
                    <StatCard
                        title="Лайки"
                        value={stats.activity?.likes ?? 0}
                        hint={`Метчей: ${stats.activity?.matches ?? 0}`}
                        color="warning"
                    />
                </Grid>
                <Grid xs={6} md={3}>
                    <StatCard
                        title="Жалобы"
                        value={stats.complaints?.total ?? 0}
                        color="danger"
                    />
                </Grid>

                <Grid xs={6} md={3}>
                    <StatCard
                        title="Принято модерацией"
                        value={stats.users?.accepted ?? 0}
                    />
                </Grid>
                <Grid xs={6} md={3}>
                    <StatCard
                        title="На модерации"
                        value={stats.users?.pending_moderation ?? 0}
                        color="neutral"
                    />
                </Grid>
                <Grid xs={6} md={3}>
                    <StatCard
                        title="Заблокированы"
                        value={stats.users?.blocked ?? 0}
                        color="danger"
                    />
                </Grid>
                <Grid xs={6} md={3}>
                    <StatCard
                        title="Контактов"
                        value={stats.activity?.contacts ?? 0}
                    />
                </Grid>
            </Grid>

            {/* Разбивки */}
            <Grid container spacing={1.5}>
                <Grid xs={12} md={6}>
                    <BreakdownList title="По полу" items={stats.profiles_breakdown?.by_gender} />
                </Grid>
                <Grid xs={12} md={6}>
                    <BreakdownList title="По возрасту" items={stats.profiles_breakdown?.by_age} />
                </Grid>
                <Grid xs={12} md={6}>
                    <BreakdownList title="По доходу" items={stats.profiles_breakdown?.by_income} />
                </Grid>
                <Grid xs={12} md={6}>
                    <BreakdownList title="По цели" items={stats.profiles_breakdown?.by_purpose} />
                </Grid>
            </Grid>

            {/* Выручка по типам */}
            {stats.revenue?.by_item_type?.length > 0 && (
                <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>Оборот по типам покупок</Typography>
                    <Stack spacing={0.5}>
                        {stats.revenue.by_item_type.map((r, i) => (
                            <Stack key={i} direction="row" justifyContent="space-between">
                                <Typography level="body-sm">
                                    {ITEM_TYPE_LABELS[r.item_type] || r.item_type}
                                </Typography>
                                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                    {money(r.total)} <Typography level="body-xs" color="neutral">({r.count})</Typography>
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>

                    {stats.revenue?.by_tariff_plan?.length > 0 && (
                        <>
                            <Typography level="title-sm" sx={{ mt: 2, mb: 1 }}>В том числе по тарифам</Typography>
                            <Stack spacing={0.5}>
                                {stats.revenue.by_tariff_plan.map((r, i) => (
                                    <Stack key={i} direction="row" justifyContent="space-between">
                                        <Typography level="body-sm">
                                            {TARIFF_LABELS[r.tariff_plan] || r.tariff_plan}
                                        </Typography>
                                        <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                            {money(r.total)} <Typography level="body-xs" color="neutral">({r.count})</Typography>
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </>
                    )}
                </Card>
            )}
        </Stack>
    );
}
